import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment, useCallback, useEffect, useMemo } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileComponent } from '~/shared/data-contract/job-profile';
import type { SkillProficiencyLevel } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import { IconArrowRightUpCircle, IconStepBack } from '~/ui/core/icons';
import { SidepanelHeader } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import type { UrlTileHost } from '../../url-tiles';
import { UrlTileSubjectActions, useTileNavigation, useUrlTileEscapeClose } from '../../url-tiles';
import { useArchiveJobLevels } from '../api/use-archive-job-levels';
import { useArchiveJobRoles } from '../api/use-archive-job-roles';
import { useInvalidateArchitecture } from '../api/use-invalidate-architecture';
import { useJobArchitectureItemPath } from '../api/use-job-architecture-item-path';
import type { JobItemDetails } from '../api/use-job-item-details';
import { LanguageWithIndicator } from '../components/LanguageWithIndicator';
import { SkeletonBreadcrumbs } from '../components/SkeletonLoaders';
import { useJobArchitecturePermissions } from '../context/JobArchitecturePermissionsContext';
import { isDetectingSkills, isTranslating } from '../hooks/use-is-generating';
import { useReviewAction } from '../hooks/use-review-action';
import { ActiveLanguageProvider, useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import {
    useLocateJobArchitectureItem,
    useNavigateToJobItemParentPage,
} from '../url-navigation/use-locate-job-architecture-item';
import {
    parseProfileVersion,
    useNavigateToStandaloneProfile,
} from '../url-navigation/use-navigate-to-standalone-profile';
import { JSA_PRIMARY_DETAIL_TILE_SLOT_KEY } from '../url-tile-payloads';
import { useJobArchitectureUrlTileActions } from '../url-tiles/use-job-architecture-url-tile-actions';
import { JobProfileContent } from './JobProfileContent';
import { ManualEditStoreProvider, useManualEditStore } from './ManualEditStoreProvider';
import { ProfileBreadcrumbs } from './ProfileBreadcrumbs';
import { SkillActionSwitcherProvider } from './SkillActionSwitcherProvider';
import { useUnsavedChangesGuard } from './use-unsaved-changes-guard';

type DetailsContentProps = {
    readonly architectureId: string;
    readonly host?: UrlTileHost;
    readonly isArchived?: boolean;
    readonly isCommentsOpen?: boolean;
    readonly isEditing: boolean;
    readonly item: JobItemDetails;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly onActiveLanguageChange?: (language: LanguageCodeForJSA) => void;
    readonly onClose?: () => void;
    readonly onToggleComments?: () => void;
    readonly parentRoleId?: string;
    readonly permissions: {
        readonly canEdit: boolean;
    };
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly profile: JobProfileComponent;
    readonly selectedVersion: string | undefined;
    readonly setActiveVersion: (versionId: string) => void;
    readonly setIsEditing: (editing: boolean) => void;
};

const Content: FunctionComponent<DetailsContentProps> = ({
    architectureId,
    host,
    isArchived,
    isCommentsOpen,
    isEditing,
    item,
    itemId,
    itemType,
    onActiveLanguageChange,
    onClose,
    onToggleComments,
    parentRoleId,
    permissions,
    profile,
    selectedVersion,
    setActiveVersion,
    setIsEditing,
}) => {
    const translate = useTranslate();
    const hasManualEdits = useManualEditStore((state) => state.hasManualEdits);
    const { navigate: navigateToStandalone } = useNavigateToStandaloneProfile();
    const { confirm } = useConfirm();
    const invalidateArchitecture = useInvalidateArchitecture();
    const { canModify } = useJobArchitecturePermissions();
    const { showErrorSnackbar } = useSnackbars();
    const { mutateAsync: archiveJobRoles } = useArchiveJobRoles();
    const { mutateAsync: archiveJobLevels } = useArchiveJobLevels();
    const { handleUnreview } = useReviewAction({ architectureId, item, itemType });
    const {
        closePrimaryDetailTile,
        openCompareTile,
        openLevelComparisonTile,
        openRoleComparisonTile,
        openSkillTile,
        updateJobProfileTileLanguage,
    } = useJobArchitectureUrlTileActions();
    const { applicationState } = useApplicationContext();
    // Levels already offer "Compare Job Levels" here; the picker entry would sit next to it as a near duplicate.
    const canCompareRoles =
        item.type === 'job-role' && applicationState.featureFlagService.isFeatureFlagEnabled('compare-job-roles');
    const { navigate: locateInJobArchitecture } = useLocateJobArchitectureItem();
    const { navigate: navigateToParentPage } = useNavigateToJobItemParentPage();
    const isMainHost = host === 'main';

    const { activeLanguage, availableLanguages, setActiveLanguage } = useActiveLanguage();

    useEffect(() => {
        onActiveLanguageChange?.(activeLanguage);
    }, [activeLanguage, onActiveLanguageChange]);

    const handleLanguageChange = useCallback(
        (language: LanguageCodeForJSA) => {
            setActiveLanguage(language);
            updateJobProfileTileLanguage(language);
        },
        [setActiveLanguage, updateJobProfileTileLanguage],
    );

    const isGeneratingTranslations = isTranslating(item?.profileGenerationInfo);
    const isGeneratingSkills = isDetectingSkills(item?.profileGenerationInfo);
    const { data: itemPath, isLoading: isItemPathLoading } = useJobArchitectureItemPath(architectureId, itemId);

    const { cancelDiscard, confirmDiscard, isDiscardDialogOpen, requestDiscard } = useUnsavedChangesGuard({
        hasUnsavedChanges: isEditing && hasManualEdits,
    });

    const handleClose = useMemo(
        () =>
            is.undefined(onClose)
                ? undefined
                : () => {
                      requestDiscard(() => {
                          setIsEditing(false);
                          onClose();
                      });
                  },
        [requestDiscard, setIsEditing, onClose],
    );

    useUrlTileEscapeClose(handleClose);

    const handleCompareVersions = useCallback(
        (versionA: string, versionB: string, preferredLanguage = activeLanguage): void => {
            openCompareTile({
                profileId: itemId,
                preferredLanguage,
                type: itemType,
                versionA,
                versionB,
            });
        },
        [activeLanguage, itemId, itemType, openCompareTile],
    );

    const handleCompareLevels = useCallback((): void => {
        openLevelComparisonTile({
            preferredLanguage: activeLanguage,
            roleId: item.type === 'job-role' ? item.id : item.parentId,
        });
    }, [activeLanguage, item, openLevelComparisonTile]);

    const handleCompareRoles = useCallback((): void => {
        openRoleComparisonTile({ itemIds: [item.id], itemType: item.type, preferredLanguage: activeLanguage });
    }, [activeLanguage, item.id, item.type, openRoleComparisonTile]);

    const handleOpenSkillDetails = useCallback(
        (
            skillId: string,
            proficiency: SkillProficiencyLevel | null | undefined,
            _availableItems: readonly { readonly id: string }[],
        ) => {
            openSkillTile(skillId, activeLanguage, proficiency);
        },
        [activeLanguage, openSkillTile],
    );

    const handleOpenSeparatePage = useCallback(() => {
        requestDiscard(() => {
            setIsEditing(false);
            navigateToStandalone({
                architectureId,
                isCommentsOpen: isCommentsOpen === true,
                itemId,
                itemType,
                preferredLanguage: activeLanguage,
                version: parseProfileVersion(selectedVersion),
            });
        });
    }, [
        requestDiscard,
        setIsEditing,
        navigateToStandalone,
        architectureId,
        isCommentsOpen,
        itemId,
        itemType,
        activeLanguage,
        selectedVersion,
    ]);

    const handleViewInJobArchitecture = useCallback(() => {
        if (is.nullOrUndefined(itemPath)) {
            return;
        }

        requestDiscard(() => {
            setIsEditing(false);
            locateInJobArchitecture({
                architectureId,
                itemId,
                itemType,
                path: itemPath,
                preferredLanguage: activeLanguage,
            });
        });
    }, [
        itemPath,
        requestDiscard,
        setIsEditing,
        locateInJobArchitecture,
        architectureId,
        itemId,
        itemType,
        activeLanguage,
    ]);

    const breadcrumbs = useMemo(() => {
        if (isMainHost || isArchived === true) {
            return undefined;
        }

        const clusterId = itemPath?.cluster;
        const familyId = itemPath?.family;

        if (isItemPathLoading || !is.nonEmptyString(clusterId) || !is.nonEmptyString(familyId)) {
            return <SkeletonBreadcrumbs />;
        }

        return (
            <ProfileBreadcrumbs
                architectureId={architectureId}
                clusterId={clusterId}
                currentItemTitle={
                    itemType === 'job-level'
                        ? translate('pages.job-architecture.job-level')
                        : translate('pages.job-architecture.job-role')
                }
                familyId={familyId}
                parentRoleId={itemType === 'job-level' ? (itemPath?.role ?? parentRoleId) : undefined}
            />
        );
    }, [architectureId, isArchived, isItemPathLoading, isMainHost, itemPath, itemType, parentRoleId, translate]);

    const originLanguageId = item.versionInfo?.translationOriginLanguage;

    const primaryDetailTileKey = `${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${architectureId}.${itemId}`;
    const tileNavigation = useTileNavigation(primaryDetailTileKey);

    const header = useMemo(
        () => (
            <SidepanelHeader
                actions={
                    <Fragment>
                        <UrlTileSubjectActions
                            subject={{
                                jobArchitectureId: architectureId,
                                kind: itemType,
                                profileId: itemId,
                                title: item.title,
                            }}
                        />
                        {!isMainHost && (
                            <Button
                                aria-label={translate('pages.job-architecture-v2.open-separate-page')}
                                iconOnly={true}
                                size="sm"
                                variant="secondary"
                                onClick={handleOpenSeparatePage}
                            >
                                <IconArrowRightUpCircle />
                            </Button>
                        )}
                        <div id="header-options-slot" />
                    </Fragment>
                }
                activeLanguage={
                    activeLanguage === undefined
                        ? undefined
                        : translate(`common.languages.${activeLanguage}` as TranslationKey)
                }
                disableArrows={is.undefined(tileNavigation)}
                isGenerating={isGeneratingTranslations || isGeneratingSkills}
                isLanguagePickerDisabled={isEditing}
                leadingAffordance={
                    isMainHost && !is.nullOrUndefined(itemPath) ? (
                        <Button
                            iconPosition="leading"
                            size="sm"
                            variant="secondary"
                            onClick={handleViewInJobArchitecture}
                        >
                            <IconStepBack />
                            {translate('pages.job-architecture-v2.standalone.view-in-job-architecture')}
                        </Button>
                    ) : undefined
                }
                onNext={tileNavigation?.onNext}
                onPrevious={tileNavigation?.onPrevious}
                languages={availableLanguages.map((language) => ({
                    ...language,
                    name: (
                        <LanguageWithIndicator
                            language={language}
                            originLanguageId={originLanguageId}
                            originLanguageLabel={translate('pages.job-architecture-v2.origin-language-label')}
                            originLanguageTooltip={translate('pages.job-architecture-v2.origin-language-tooltip')}
                            translatedLanguageTooltip={translate(
                                'pages.job-architecture-v2.translated-with-ai-tooltip',
                            )}
                        />
                    ),
                }))}
                setActiveLanguage={handleLanguageChange}
                onClose={handleClose}
            />
        ),
        [
            architectureId,
            handleOpenSeparatePage,
            handleViewInJobArchitecture,
            isMainHost,
            item.title,
            itemId,
            itemType,
            itemPath,
            translate,
            activeLanguage,
            isGeneratingTranslations,
            isGeneratingSkills,
            isEditing,
            availableLanguages,
            originLanguageId,
            handleLanguageChange,
            handleClose,
            tileNavigation,
        ],
    );

    const handleArchive = useCallback(async (): Promise<void> => {
        try {
            await confirm({
                description: (
                    <Fragment>
                        {item.type === 'job-role'
                            ? translate('pages.job-architecture-v2.archive-job-roles-description')
                            : translate('pages.job-architecture-v2.archive-job-levels-description')}
                        <ul>
                            <li key={item.id}>{item.title}</li>
                        </ul>
                    </Fragment>
                ),
                title: translate('pages.job-architecture-v2.archive-title'),
                type: 'default',
            });
        } catch {
            return;
        }

        try {
            await (item.type === 'job-role'
                ? archiveJobRoles({ jobRoles: [item] })
                : archiveJobLevels({ jobLevels: [item] }));
        } catch (error) {
            showErrorSnackbar(error);
            invalidateArchitecture(architectureId);
            return;
        }

        if (isMainHost) {
            navigateToParentPage({ architectureId, path: itemPath ?? {} });
            return;
        }

        closePrimaryDetailTile();
    }, [
        confirm,
        translate,
        item,
        archiveJobRoles,
        archiveJobLevels,
        isMainHost,
        navigateToParentPage,
        architectureId,
        itemPath,
        closePrimaryDetailTile,
        showErrorSnackbar,
        invalidateArchitecture,
    ]);

    const commentable = useMemo(
        () =>
            is.undefined(onToggleComments)
                ? undefined
                : { isOpen: isCommentsOpen ?? false, onToggle: onToggleComments },
        [isCommentsOpen, onToggleComments],
    );

    return (
        <Fragment>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.confirm')}
                confirmButtonVariant="default"
                description={translate('dialogs.discard.content')}
                open={isDiscardDialogOpen}
                title={translate('dialogs.discard.title')}
                onCancel={cancelDiscard}
                onConfirm={confirmDiscard}
            />
            <JobProfileContent
                architectureId={architectureId}
                breadcrumbs={breadcrumbs}
                commentable={commentable}
                header={header}
                hideNotifications={isMainHost}
                isArchived={isArchived}
                isEditing={isEditing}
                item={item}
                itemId={itemId}
                itemType={itemType}
                parentRoleId={parentRoleId}
                permissions={permissions}
                profile={profile}
                selectedVersion={selectedVersion}
                setActiveVersion={setActiveVersion}
                setIsEditing={setIsEditing}
                onArchive={
                    canModify && permissions.canEdit && item.actions?.canEdit === true ? handleArchive : undefined
                }
                onCompareLevels={handleCompareLevels}
                onCompareRoles={canCompareRoles ? handleCompareRoles : undefined}
                onCompareVersions={handleCompareVersions}
                onOpenSeparatePage={isMainHost ? undefined : handleOpenSeparatePage}
                onOpenSkillDetails={handleOpenSkillDetails}
                onUnreview={
                    item.versionInfo?.isReviewed === true && item.versionInfo.isPublished === false
                        ? handleUnreview
                        : undefined
                }
            />
        </Fragment>
    );
};

export const DetailsContent: FunctionComponent<DetailsContentProps> = (props) => {
    return (
        <ActiveLanguageProvider preferredLanguage={props.preferredLanguage} profiles={[props.profile]}>
            <SkillActionSwitcherProvider>
                <ManualEditStoreProvider>
                    <Content {...props} />
                </ManualEditStoreProvider>
            </SkillActionSwitcherProvider>
        </ActiveLanguageProvider>
    );
};
